import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { contentAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import { formatMarathiDate } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminNotices.css';

const apiErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  if (Array.isArray(data?.details) && data.details.length) {
    return data.details.map(item => item.message).join(', ');
  }
  return data?.error || fallback;
};

const E_DAWANDI_CATEGORIES = [
  { value: 'निधन वार्ता / शोक संदेश', label: '🕊️ निधन वार्ता / शोक संदेश' },
  { value: 'पाणीपुरवठा', label: '💧 पाणीपुरवठा सूचना' },
  { value: 'वीजपुरवठा', label: '⚡ वीजपुरवठा सूचना' },
  { value: 'आरोग्य / लसीकरण', label: '🏥 आरोग्य / लसीकरण शिबिर' },
  { value: 'आपत्कालीन', label: '🚨 आपत्कालीन सूचना' },
  { value: 'इतर', label: '📢 इतर महत्त्वाची सूचना' },
];

const buildNoticePayload = formData => ({
  title: formData.title.trim(),
  content: formData.content.trim(),
  category: formData.category || 'general',
  sub_category: formData.is_e_dawandi ? (formData.sub_category || 'इतर') : null,
  priority: formData.priority || 'normal',
  publish_date: formData.publish_date || null,
  expiry_date: formData.expiry_date || null,
  is_published: formData.is_published !== false,
  attachment_url: formData.attachment_url || null,
  is_e_dawandi: Boolean(formData.is_e_dawandi),
  broadcast_whatsapp: Boolean(formData.broadcast_whatsapp),
  recipient_phones: formData.broadcast_whatsapp && formData.target_audience === 'specific' ? (formData.recipient_phones || []) : null,
});

export default function AdminNotices() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'notices', user);

  const [activeTab, setActiveTab] = useState('public'); // 'public' | 'e_dawandi'
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useBackButtonClose(showForm, () => { setShowForm(false); setEditItem(null); });

  const emptyForm = {
    title: '',
    content: '',
    category: 'general',
    sub_category: 'निधन वार्ता / शोक संदेश',
    priority: 'normal',
    publish_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    is_published: true,
    attachment_url: '',
    is_e_dawandi: false,
    broadcast_whatsapp: false,
    target_audience: 'all', // 'all' | 'specific'
    recipient_phones: [],
  };
  const [formData, setFormData] = useState(emptyForm);

  // Recipient targeting state
  const [internalRecipients, setInternalRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientSearchText, setRecipientSearchText] = useState('');
  const [searchedRecipients, setSearchedRecipients] = useState([]);
  const [searchingRecipients, setSearchingRecipients] = useState(false);
  const [selectedExtraRecipients, setSelectedExtraRecipients] = useState([]);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await contentAPI.getNotices(slug);
      setNotices(res.data.notices || []);
    } catch (err) {
      setError('लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadRecipients = useCallback(async () => {
    if (!slug) return;
    setLoadingRecipients(true);
    try {
      const res = await contentAPI.getNoticeRecipients(slug);
      setInternalRecipients(res.data?.internal_recipients || []);
    } catch (_err) {
      // Graceful fallback
    } finally {
      setLoadingRecipients(false);
    }
  }, [slug]);

  useEffect(() => {
    if (showForm && formData.broadcast_whatsapp && formData.target_audience === 'specific' && internalRecipients.length === 0) {
      loadRecipients();
    }
  }, [showForm, formData.broadcast_whatsapp, formData.target_audience, internalRecipients.length, loadRecipients]);

  useEffect(() => {
    const q = recipientSearchText.trim();
    if (!q || q.length < 2) {
      setSearchedRecipients([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingRecipients(true);
      try {
        const res = await contentAPI.getNoticeRecipients(slug, { search: q });
        // Exclude internal staff already shown in the checklist
        const internalPhones = new Set(internalRecipients.map(r => r.phone));
        const filtered = (res.data?.recipients || []).filter(r => !internalPhones.has(r.phone));
        setSearchedRecipients(filtered);
      } catch (_err) {
        setSearchedRecipients([]);
      } finally {
        setSearchingRecipients(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [recipientSearchText, slug, internalRecipients]);

  const handleToggleInternalRecipient = (phone) => {
    const current = formData.recipient_phones || [];
    const exists = current.includes(phone);
    const updated = exists ? current.filter(p => p !== phone) : [...current, phone];
    setFormData(prev => ({ ...prev, recipient_phones: updated }));
  };

  const handleSelectAllInternal = () => {
    const internalPhones = internalRecipients.map(r => r.phone).filter(Boolean);
    const existingOther = (formData.recipient_phones || []).filter(p => !internalPhones.includes(p));
    setFormData(prev => ({ ...prev, recipient_phones: [...existingOther, ...internalPhones] }));
  };

  const handleDeselectAllInternal = () => {
    const internalPhones = new Set(internalRecipients.map(r => r.phone).filter(Boolean));
    const remaining = (formData.recipient_phones || []).filter(p => !internalPhones.has(p));
    setFormData(prev => ({ ...prev, recipient_phones: remaining }));
  };

  const handleAddSearchedRecipient = (recipient) => {
    if (!recipient?.phone) return;
    const current = formData.recipient_phones || [];
    if (!current.includes(recipient.phone)) {
      setFormData(prev => ({ ...prev, recipient_phones: [...current, recipient.phone] }));
      setSelectedExtraRecipients(prev => {
        if (prev.some(r => r.phone === recipient.phone)) return prev;
        return [...prev, recipient];
      });
    }
    setRecipientSearchText('');
    setSearchedRecipients([]);
  };

  const handleRemoveExtraRecipient = (phone) => {
    setFormData(prev => ({
      ...prev,
      recipient_phones: (prev.recipient_phones || []).filter(p => p !== phone),
    }));
    setSelectedExtraRecipients(prev => prev.filter(r => r.phone !== phone));
  };

  const handleOpenCreate = () => {
    setEditItem(null);
    setFormData({
      ...emptyForm,
      is_e_dawandi: activeTab === 'e_dawandi',
      priority: activeTab === 'e_dawandi' ? 'urgent' : 'normal',
      sub_category: 'निधन वार्ता / शोक संदेश',
      is_published: activeTab !== 'e_dawandi', // default true for standard notices, false for e-dawandi
      broadcast_whatsapp: activeTab === 'e_dawandi', // default checked for E-Dawandi
      target_audience: 'all',
      recipient_phones: [],
    });
    setSelectedExtraRecipients([]);
    setRecipientSearchText('');
    setSearchedRecipients([]);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      const url = res.data?.url || res.data?.fileUrl;
      if (url) {
        setFormData(prev => ({ ...prev, attachment_url: url }));
      } else {
        setError('फाईल अपलोड अयशस्वी: URL सापडला नाही');
      }
    } catch (err) {
      setError('फाईल अपलोड अयशस्वी: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.publish_date && formData.expiry_date && formData.publish_date > formData.expiry_date) {
      setError('मुदत संपण्याची तारीख प्रकाशन तारखेपूर्वी असू शकत नाही.');
      return;
    }
    if (formData.broadcast_whatsapp && formData.target_audience === 'specific' && (!formData.recipient_phones || formData.recipient_phones.length === 0)) {
      setError('कृपया किमान एका व्यक्तीला निवडा किंवा "सर्व व्यक्तींना पाठवा" पर्याय निवडा.');
      return;
    }
    try {
      const payload = buildNoticePayload(formData);
      if (editItem) {
        await contentAPI.updateNotice(slug, editItem.id, payload);
        setSuccess(formData.is_e_dawandi ? 'ई-दवंडी अद्ययावत केली!' : 'सूचना अद्ययावत केली!');
      } else {
        await contentAPI.createNotice(slug, payload);
        const recipientNote = formData.broadcast_whatsapp
          ? (formData.target_audience === 'specific'
              ? ` आणि निवडक ${formData.recipient_phones.length} व्यक्तींना WhatsApp वर पाठवली जात आहे!`
              : ' आणि सर्व व्यक्तींना WhatsApp वर पाठवली जात आहे!')
          : '!';
        if (!formData.is_published && formData.broadcast_whatsapp) {
          setSuccess(
            formData.is_e_dawandi
              ? `ई-दवंडी केवळ WhatsApp वर पाठवली जात आहे (पोर्टलवर अप्रकाशित)${recipientNote}`
              : `सूचना केवळ WhatsApp वर पाठवली जात आहे (नागरिक पोर्टलवर अप्रकाशित)${recipientNote}`
          );
        } else if (!formData.is_published && !formData.broadcast_whatsapp) {
          setSuccess('सूचना ड्राफ्ट (अप्रकाशित) म्हणून सेव्ह केली!');
        } else {
          setSuccess(
            formData.is_e_dawandi
              ? `ई-दवंडी तयार केली${recipientNote}`
              : `सूचना तयार केली${recipientNote}`
          );
        }
      }
      setShowForm(false);
      setEditItem(null);
      setFormData(emptyForm);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'अयशस्वी'));
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category || 'general',
      sub_category: item.sub_category || 'निधन वार्ता / शोक संदेश',
      priority: item.priority || 'normal',
      publish_date: (item.publish_date || item.published_date)?.split('T')[0] || '',
      expiry_date: item.expiry_date?.split('T')[0] || '',
      is_published: item.is_published !== false && item.is_active !== false,
      attachment_url: item.attachment_url || '',
      is_e_dawandi: Boolean(item.is_e_dawandi),
      broadcast_whatsapp: false, // User can explicitly opt-in to re-broadcast on edit
      target_audience: 'all',
      recipient_phones: [],
    });
    setSelectedExtraRecipients([]);
    setRecipientSearchText('');
    setSearchedRecipients([]);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ही नोंद हटवायची आहे का?')) return;
    try {
      await contentAPI.deleteNotice(slug, id);
      setSuccess('नोंद हटवली');
      loadData();
    } catch (err) {
      setError('हटवण्यात अयशस्वी');
    }
  };

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [publishFilter, setPublishFilter] = useState('');
  const [sort, setSort] = useState({ key: 'publish_date', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const publicCount = useMemo(() => notices.filter(n => !n.is_e_dawandi).length, [notices]);
  const eDawandiCount = useMemo(() => notices.filter(n => Boolean(n.is_e_dawandi)).length, [notices]);

  const filteredNotices = useMemo(() => {
    return notices.filter(n => {
      const matchTab = activeTab === 'e_dawandi' ? Boolean(n.is_e_dawandi) : !n.is_e_dawandi;
      if (!matchTab) return false;
      if (search && !n.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter && n.category !== categoryFilter) return false;
      if (priorityFilter && n.priority !== priorityFilter) return false;
      if (publishFilter) {
        const isPub = n.is_published !== false && n.is_active !== false;
        if ((publishFilter === 'published') !== isPub) return false;
      }
      return true;
    }).sort((a, b) => {
      const dateKeys = new Set(['publish_date', 'expiry_date']);
      const left = dateKeys.has(sort.key) ? new Date(a[sort.key] || a.published_date || 0).getTime() : String(a[sort.key] ?? '').toLowerCase();
      const right = dateKeys.has(sort.key) ? new Date(b[sort.key] || b.published_date || 0).getTime() : String(b[sort.key] ?? '').toLowerCase();
      return (left > right ? 1 : left < right ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [notices, activeTab, search, categoryFilter, priorityFilter, publishFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredNotices.length / pageSize));
  const visibleNotices = filteredNotices.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const changeSort = key => {
    setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const SortHeader = ({ column, children }) => (
    <button className={`notice-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>
      {children}
      <span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const exportCsv = () => {
    const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Title', 'Category', 'Sub Category', 'Priority', 'Publish date', 'Expiry date', 'Published', 'Attachment'],
      ...filteredNotices.map(n => [
        n.title,
        n.category,
        n.sub_category || '',
        n.priority,
        n.publish_date || n.published_date,
        n.expiry_date,
        n.is_published !== false && n.is_active !== false ? 'Yes' : 'No',
        n.attachment_url || '',
      ])
    ];
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['\ufeff', rows.map(row => row.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `${activeTab === 'e_dawandi' ? 'e-dawandi' : 'notices'}-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="admin-card-header">
        <div>
          <h2>{activeTab === 'e_dawandi' ? '📢 ई-दवंडी (WhatsApp ब्रॉडकास्ट)' : '📋 सार्वजनिक सूचना फलक'}</h2>
          <small style={{ color: '#64748b' }}>
            {activeTab === 'e_dawandi'
              ? 'निधन वार्ता, आपत्कालीन व त्वरित संदेश थेट नागरिकांच्या WhatsApp वर पाठवा (वेबसाइटवर दिसणार नाही)'
              : 'सार्वजनिक वेबसाइटवरील अधिकृत सूचना व परिपत्रके'}
          </small>
        </div>
        {canWriteThis && (
          <button className="btn btn-primary" onClick={showForm ? () => setShowForm(false) : handleOpenCreate}>
            {showForm ? 'रद्द करा' : (activeTab === 'e_dawandi' ? '+ नवीन ई-दवंडी' : '+ नवीन सूचना')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="notice-tabs">
        <button
          className={`notice-tab-btn ${activeTab === 'public' ? 'active' : ''}`}
          onClick={() => { setActiveTab('public'); setShowForm(false); setPage(1); }}
        >
          📋 सार्वजनिक सूचना <span className="notice-tab-badge">{publicCount}</span>
        </button>
        <button
          className={`notice-tab-btn ${activeTab === 'e_dawandi' ? 'active' : ''}`}
          onClick={() => { setActiveTab('e_dawandi'); setShowForm(false); setPage(1); }}
        >
          📢 ई-दवंडी (WhatsApp Only) <span className="notice-tab-badge">{eDawandiCount}</span>
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>
            {editItem
              ? (formData.is_e_dawandi ? 'ई-दवंडी संपादन' : 'सूचना संपादन')
              : (formData.is_e_dawandi ? 'नवीन ई-दवंडी पाठवा' : 'नवीन सूचना तयार करा')}
          </h3>

          {formData.is_e_dawandi && (
            <div className="e-dawandi-banner">
              🔒 <strong>गोपनीयता सूचना:</strong> ही ई-दवंडी केवळ WhatsApp द्वारे नागरिकांना पाठवली जाईल. ती ग्रामपंचायतीच्या सार्वजनिक वेबसाइटच्या सूचना फलकावर <strong>दिसणार नाही</strong>.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>शीर्षक *</label>
                <input
                  value={formData.title}
                  placeholder={formData.is_e_dawandi ? 'उदा. अत्यंत दुःखद निधन वार्ता - कै. ...' : 'उदा. ग्रामपंचायत विशेष सूचना...'}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              {formData.is_e_dawandi ? (
                <div className="form-group">
                  <label>ई-दवंडी प्रकार (उपवर्ग) *</label>
                  <select
                    value={formData.sub_category}
                    onChange={e => setFormData({...formData, sub_category: e.target.value})}
                    required
                  >
                    {E_DAWANDI_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>वर्ग</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="general">सामान्य</option>
                    <option value="meeting">बैठक</option>
                    <option value="scheme">योजना</option>
                    <option value="tax">कर</option>
                    <option value="event">कार्यक्रम</option>
                    <option value="tender">निविदा</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>प्राधान्य</label>
                <select
                  value={formData.priority}
                  onChange={e => setFormData({...formData, priority: e.target.value})}
                >
                  <option value="normal">सामान्य</option>
                  <option value="important">महत्त्वाचे</option>
                  <option value="urgent">तातडीचे / अत्यंत महत्त्वाचे</option>
                </select>
              </div>

              <div className="form-group">
                <label>तारीख / प्रकाशन तारीख</label>
                <input
                  type="date"
                  max={formData.expiry_date || undefined}
                  value={formData.publish_date}
                  onChange={e => setFormData({...formData, publish_date: e.target.value})}
                />
              </div>

              {!formData.is_e_dawandi && (
                <div className="form-group">
                  <label>मुदत संपण्याची तारीख</label>
                  <input
                    type="date"
                    min={formData.publish_date || undefined}
                    value={formData.expiry_date}
                    onChange={e => setFormData({...formData, expiry_date: e.target.value})}
                  />
                </div>
              )}

              {/* Attachment upload */}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>📎 संलग्नीत फाईल (PDF किंवा फोटो/प्रतिमा - ऐच्छिक)</label>
                <div className="attachment-upload-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    {uploading && <span style={{ fontSize: 12, color: '#0284c7' }}>⏳ फाईल अपलोड होत आहे...</span>}
                    {formData.attachment_url && (
                      <div className="attachment-preview-badge">
                        <span>✓ फाईल जोडली:</span>
                        <a href={formData.attachment_url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: '#0369a1' }}>
                          पहा / डाऊनलोड
                        </a>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, attachment_url: '' })}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}
                          title="काढून टाका"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                    * PDF परिपत्रक किंवा माहितीचा फोटो जोडू शकता. WhatsApp वर नागरिकांना हा फोटो/PDF थेट पाठवला जाईल.
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>मजकूर / संदेश *</label>
                <textarea
                  rows="5"
                  value={formData.content}
                  placeholder={
                    formData.is_e_dawandi
                      ? 'नागरिकांसाठी संपूर्ण संदेश येथे लिहा (उदा. अंत्यविधीची वेळ, स्थळ किंवा दुरुस्तीची माहिती...)'
                      : 'सूचनेचा संपूर्ण तपशील...'
                  }
                  onChange={e => setFormData({...formData, content: e.target.value})}
                  required
                />
              </div>

              {/* Distribution & Publishing Channels */}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <div className="notice-channels-wrapper">
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📢 प्रसिद्धी व वितरण माध्यमे (Distribution & Publishing Channels)</span>
                  </div>

                  {/* Channel 1: Citizen Portal */}
                  <div className={`notice-channel-card ${formData.is_published ? 'active-portal' : ''}`}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600, color: formData.is_published ? '#0369a1' : '#64748b', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.is_published}
                        onChange={e => setFormData({ ...formData, is_published: e.target.checked })}
                        style={{ width: 18, height: 18 }}
                      />
                      <span style={{ fontSize: 13 }}>
                        🌐 नागरिक पोर्टलवर प्रकाशित करा (Publish on Citizen Portal)
                      </span>
                    </label>
                    <div style={{ fontSize: 11, color: formData.is_published ? '#0284c7' : '#94a3b8', marginTop: 4, marginLeft: 28 }}>
                      {formData.is_published
                        ? '✓ ही सूचना ग्रामपंचायत नागरिक पोर्टलवर सर्व नागरिकांसाठी खुली दिसेल.'
                        : '⚠️ ही सूचना नागरिक पोर्टलवर दिसणार नाही (केवळ WhatsApp द्वारे पाठवली जाईल किंवा अंतर्गत ड्राफ्ट राहील).'}
                    </div>
                  </div>

                  {/* Channel 2: WhatsApp Broadcast */}
                  <div className={`notice-channel-card ${formData.broadcast_whatsapp ? 'active-whatsapp' : ''}`}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600, color: formData.broadcast_whatsapp ? '#166534' : '#64748b', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.broadcast_whatsapp}
                        onChange={e => setFormData({ ...formData, broadcast_whatsapp: e.target.checked })}
                        style={{ width: 18, height: 18 }}
                      />
                      <span style={{ fontSize: 13 }}>
                        📱 WhatsApp द्वारे सूचना पाठवा (Send via WhatsApp)
                        {formData.is_e_dawandi && ' (ई-दवंडीसाठी शिफारस केलेले)'}
                      </span>
                    </label>
                    <div style={{ fontSize: 11, color: formData.broadcast_whatsapp ? '#15803d' : '#94a3b8', marginTop: 4, marginLeft: 28 }}>
                      {formData.broadcast_whatsapp ? (
                        formData.attachment_url
                          ? '✓ संलग्नीत फाईलसह (PDF/Image) WhatsApp वर ब्रॉडकास्ट टेम्पलेट पाठवले जाईल.'
                          : '✓ फक्त मजकूर स्वरूपात WhatsApp वर ब्रॉडकास्ट टेम्पलेट पाठवले जाईल.'
                      ) : (
                        'WhatsApp द्वारे कोणतीही सूचना पाठवली जाणार नाही.'
                      )}
                    </div>

                    {/* Audience Selection when WhatsApp is checked */}
                    {formData.broadcast_whatsapp && (
                      <div className="recipient-selector-box">
                        <div className="recipient-audience-radios">
                          <label className="recipient-audience-radio-label">
                            <input
                              type="radio"
                              name="target_audience"
                              value="all"
                              checked={formData.target_audience === 'all'}
                              onChange={() => setFormData({ ...formData, target_audience: 'all' })}
                            />
                            <span>🌐 सर्व नोंदणीकृत व्यक्तींना पाठवा (Send to All)</span>
                          </label>
                          <label className="recipient-audience-radio-label">
                            <input
                              type="radio"
                              name="target_audience"
                              value="specific"
                              checked={formData.target_audience === 'specific'}
                              onChange={() => setFormData({ ...formData, target_audience: 'specific' })}
                            />
                            <span>🎯 निवडक व्यक्तींना पाठवा (Select Specific)</span>
                          </label>
                        </div>

                        {/* Specific Recipient Picker */}
                        {formData.target_audience === 'specific' && (
                          <div style={{ marginTop: 12 }}>
                            {/* Section 1: Internal staff and board members with checkboxes */}
                            <div className="recipient-section-title">
                              <span>🏢 ग्रामपंचायत कर्मचारी व पदाधिकारी (Internal Users & Staff)</span>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  type="button"
                                  className="recipient-quick-btn"
                                  onClick={handleSelectAllInternal}
                                >
                                  ✓ सर्व कर्मचारी निवडा
                                </button>
                                <button
                                  type="button"
                                  className="recipient-quick-btn"
                                  onClick={handleDeselectAllInternal}
                                >
                                  ✕ सर्व अनचेक करा
                                </button>
                              </div>
                            </div>

                            {loadingRecipients ? (
                              <div style={{ padding: 12, fontSize: 12, color: '#64748b' }}>
                                कर्मचारी यादी लोड होत आहे...
                              </div>
                            ) : internalRecipients.length === 0 ? (
                              <div style={{ padding: 10, fontSize: 12, color: '#64748b' }}>
                                कोणतेही कर्मचारी किंवा पदाधिकारी सापडले नाहीत.
                              </div>
                            ) : (
                              <div className="recipient-checkbox-grid">
                                {internalRecipients.map(person => {
                                  const isChecked = (formData.recipient_phones || []).includes(person.phone);
                                  return (
                                    <label
                                      key={person.phone}
                                      className={`recipient-checkbox-item ${isChecked ? 'selected' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggleInternalRecipient(person.phone)}
                                      />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {person.citizenName}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                          <span className="recipient-badge-designation">
                                            {person.designation || person.category}
                                          </span>
                                          <span style={{ fontSize: 10, color: '#64748b' }}>
                                            {person.phone}
                                          </span>
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {/* Section 2: Search any other citizen / business user */}
                            <div className="recipient-section-title" style={{ marginTop: 14 }}>
                              <span>🔍 इतर नागरिक / व्यावसायिक शोधा व जोडा (Search Citizens & Businesses)</span>
                            </div>
                            <div className="recipient-search-wrapper">
                              <input
                                type="text"
                                className="recipient-search-input"
                                placeholder="नागरिकाचे नाव किंवा मोबाईल नंबर टाकून शोधा..."
                                value={recipientSearchText}
                                onChange={e => setRecipientSearchText(e.target.value)}
                              />
                              {searchingRecipients && (
                                <span style={{ position: 'absolute', right: 12, top: 10, fontSize: 11, color: '#64748b' }}>
                                  शोधत आहे...
                                </span>
                              )}

                              {/* Search Dropdown Results */}
                              {searchedRecipients.length > 0 && (
                                <div className="recipient-search-dropdown">
                                  {searchedRecipients.map(r => {
                                    const isAlreadySelected = (formData.recipient_phones || []).includes(r.phone);
                                    return (
                                      <div
                                        key={r.phone}
                                        className="recipient-search-result-item"
                                        onClick={() => !isAlreadySelected && handleAddSearchedRecipient(r)}
                                        style={{ opacity: isAlreadySelected ? 0.5 : 1, cursor: isAlreadySelected ? 'default' : 'pointer' }}
                                      >
                                        <div>
                                          <strong style={{ color: '#0f172a' }}>{r.citizenName}</strong>
                                          <span style={{ marginLeft: 8, fontSize: 11, color: '#475569' }}>
                                            ({r.designation || r.category || 'नागरिक'})
                                          </span>
                                          <div style={{ fontSize: 11, color: '#64748b' }}>📱 {r.phone}</div>
                                        </div>
                                        <div>
                                          {isAlreadySelected ? (
                                            <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ निवडले आहे</span>
                                          ) : (
                                            <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>+ जोडा</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Selected Extra Citizens Chips */}
                            {selectedExtraRecipients.length > 0 && (
                              <div style={{ marginTop: 6 }}>
                                <div style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
                                  जोडलेले इतर नागरिक / व्यावसायिक:
                                </div>
                                <div className="recipient-chips-container">
                                  {selectedExtraRecipients.map(r => (
                                    <span key={r.phone} className="recipient-chip">
                                      <span>👤 {r.citizenName} ({r.phone})</span>
                                      <button
                                        type="button"
                                        className="recipient-chip-remove"
                                        onClick={() => handleRemoveExtraRecipient(r.phone)}
                                        title="काढा"
                                      >
                                        ✕
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Live Total Selected Summary */}
                            <div className="recipient-summary-badge">
                              <span>
                                ✓ एकूण निवडलेले: <strong>{formData.recipient_phones?.length || 0}</strong> व्यक्ती
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Summary Status Banner */}
                  {!formData.is_published && formData.broadcast_whatsapp && (
                    <div className="notice-channel-banner mode-whatsapp-only">
                      <span>⚡ <strong>WhatsApp-Only सूचना:</strong> ही सूचना नागरिक पोर्टलवर प्रसिद्ध होणार नाही, केवळ {formData.target_audience === 'specific' ? `निवडक (${formData.recipient_phones?.length || 0})` : 'सर्व'} WhatsApp व्यक्तींना पाठवली जाईल.</span>
                    </div>
                  )}
                  {formData.is_published && formData.broadcast_whatsapp && (
                    <div className="notice-channel-banner mode-both">
                      <span>✓ <strong>पोर्टल + WhatsApp:</strong> सूचना पोर्टलवर प्रसिद्ध होईल आणि WhatsApp वरही पाठवली जाईल.</span>
                    </div>
                  )}
                  {formData.is_published && !formData.broadcast_whatsapp && (
                    <div className="notice-channel-banner mode-portal-only">
                      <span>✓ <strong>केवळ नागरिक पोर्टल:</strong> सूचना पोर्टलवर प्रकाशित होईल (WhatsApp मेसेज पाठवला जाणार नाही).</span>
                    </div>
                  )}
                  {!formData.is_published && !formData.broadcast_whatsapp && (
                    <div className="notice-channel-banner mode-draft">
                      <span>ℹ️ <strong>अप्रकाशित ड्राफ्ट:</strong> ही सूचना पोर्टलवर दिसणार नाही आणि WhatsApp वरही पाठवली जाणार नाही.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-form-actions" style={{ marginTop: 20 }}>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {editItem ? 'अद्ययावत करा' : (formData.is_e_dawandi ? '📢 ई-दवंडी पाठवा' : 'सूचना तयार करा')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setShowForm(false); setEditItem(null); setFormData(emptyForm); }}
              >
                रद्द करा
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="admin-card notice-table-card">
          <div className="notice-table-toolbar">
            <label className="notice-search">
              <span>⌕</span>
              <input
                placeholder="शीर्षकाने शोधा..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </label>

            {activeTab === 'public' ? (
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व वर्ग</option>
                <option value="general">सामान्य</option>
                <option value="meeting">बैठक</option>
                <option value="scheme">योजना</option>
                <option value="tax">कर</option>
                <option value="event">कार्यक्रम</option>
                <option value="tender">निविदा</option>
              </select>
            ) : null}

            <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}>
              <option value="">सर्व प्राधान्य</option>
              <option value="normal">सामान्य</option>
              <option value="important">महत्त्वाचे</option>
              <option value="urgent">तातडीचे</option>
            </select>

            {activeTab === 'public' && (
              <select value={publishFilter} onChange={e => { setPublishFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व स्थिती (All Status)</option>
                <option value="published">🌐 पोर्टलवर प्रकाशित</option>
                <option value="draft">🔒 पोर्टलवर नाही / WhatsApp Only</option>
              </select>
            )}

            <button className="notice-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
          </div>

          <div className="notice-table-meta">
            <span>
              <strong>{filteredNotices.length}</strong> {activeTab === 'e_dawandi' ? 'ई-दवंडी नोंदी' : 'सूचना नोंदी'}
            </span>
            <span className={`notice-load-status ${loading ? 'visible' : ''}`}>
              <i /> माहिती अद्ययावत होत आहे
            </span>
          </div>

          <div className={`table-scroll notice-table-scroll ${loading ? 'is-refreshing' : ''}`}>
            <table className="data-table notice-data-table">
              <thead>
                <tr>
                  <th><SortHeader column="title">शीर्षक</SortHeader></th>
                  <th>{activeTab === 'e_dawandi' ? 'प्रकार / उपवर्ग' : <SortHeader column="category">वर्ग</SortHeader>}</th>
                  <th><SortHeader column="priority">प्राधान्य</SortHeader></th>
                  <th><SortHeader column="publish_date">तारीख</SortHeader></th>
                  <th>संलग्नीत फाईल</th>
                  <th><SortHeader column="is_published">वितरण व स्थिती</SortHeader></th>
                  <th>कृती</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSpinner.TableRow colSpan={7} message={activeTab === 'e_dawandi' ? 'ई-दवंडी लोड होत आहे...' : 'सूचना लोड होत आहेत...'} />
                ) : visibleNotices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="notice-empty">
                      {activeTab === 'e_dawandi' ? 'ई-दवंडी नोंदी आढळल्या नाहीत.' : 'सूचना आढळल्या नाहीत.'}
                    </td>
                  </tr>
                ) : (
                  visibleNotices.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <div className="notice-title-cell">
                        <strong>{n.title}</strong>
                        <small>{String(n.content || '').replace(/<[^>]+>/g, '').slice(0, 80)}</small>
                      </div>
                    </td>
                    <td>
                      <span className="notice-category">
                        {activeTab === 'e_dawandi' ? (n.sub_category || 'ई-दवंडी') : n.category}
                      </span>
                    </td>
                    <td>
                      <span className={`notice-priority priority-${n.priority}`}>{n.priority}</span>
                    </td>
                    <td className="notice-date">
                      {(n.publish_date || n.published_date) ? formatMarathiDate(n.publish_date || n.published_date) : '-'}
                    </td>
                    <td>
                      {n.attachment_url ? (
                        <a
                          href={n.attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11, color: '#0284c7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          📎 <span>फाईल पहा</span>
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {n.is_published !== false && n.is_active !== false ? (
                          <span className="notice-publish published" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            🌐 पोर्टलवर
                          </span>
                        ) : (
                          <span
                            className="notice-publish draft"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
                            title="नागरिक पोर्टलवर अप्रकाशित"
                          >
                            🔒 पोर्टलवर नाही
                          </span>
                        )}
                        {(activeTab === 'e_dawandi' || n.broadcast_whatsapp !== false || n.is_e_dawandi) && (
                          <span className="whatsapp-badge" style={{ fontSize: 9 }}>
                            📱 WhatsApp
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {canWriteThis && (
                          <button className="notice-edit-btn" onClick={() => handleEdit(n)}>संपादन</button>
                        )}
                        {canWriteThis && (
                          <button className="notice-delete-btn" onClick={() => handleDelete(n.id)}>हटवा</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>

          <div className="table-pagination notice-pagination">
            <label>
              प्रति पृष्ठ{' '}
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
            </label>
            <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(value => Math.max(1, value - 1))}>
              ← मागील
            </button>
            <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
            <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>
              पुढील →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
